import { useCallback } from 'react';
import {
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useMutation,
} from '@tanstack/react-query';
import { v4 as uuid4 } from 'uuid';
import {
  ExtendedMessagePayload,
  Message,
  MessageStatus,
  MessageType,
} from '@/types/message.types';
import { axiosClient } from '@/utils/apiClient';
import { getMessageTemplate } from '@/utils/message';
import { useMessagesStore } from './useMessagesStore';
import { useMessagesStoreInit } from './useMessagesStoreInit';
import { useSender } from './useSender';
import { useSenderStore } from './useSenderStore';
import { useWidgetConfig } from './useWidgetConfig';

export const MESSAGES_QUERY_LIMIT = 50;
export const useMessagesQuery = (): UseInfiniteQueryResult<
  Message[],
  Error
> => {
  const { senderId } = useSender();
  const { config } = useWidgetConfig();
  const { messages, actions: messagesActions } = useMessagesStore();
  const messagesStoreHydrated = useMessagesStoreInit();
  const channelIdentityId = config?.channelIdentityId;

  return useInfiniteQuery({
    queryKey: ['messages', senderId, channelIdentityId],
    queryFn: async ({ pageParam }): Promise<Message[]> => {
      const params = new URLSearchParams({
        offset: pageParam.toString(),
        limit: MESSAGES_QUERY_LIMIT.toString(),
        order: 'desc',
      });

      const response = await axiosClient.get(
        `/LiveChatV2/Public/Senders/${senderId}/Channels/${channelIdentityId}/Messages?${params}`,
      );

      if (response.data?.length) {
        messagesActions.prependMessages(response.data);
      }

      return response.data as Message[];
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < MESSAGES_QUERY_LIMIT) {
        return undefined;
      }
      // use messages.length instead of lastPageParam + MESSAGES_QUERY_LIMIT
      // to cover new messages sent hence messages length and index has changed
      return messages.length;
    },
    initialPageParam: 0,
    enabled: !!senderId && !!channelIdentityId && messagesStoreHydrated,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

export const useMessagesMutation = () => {
  const { senderId } = useSender();
  const { config } = useWidgetConfig();
  const { messagesMap, actions: messagesActions } = useMessagesStore();
  const { actions: senderActions } = useSenderStore();
  const channelIdentityId = config?.channelIdentityId;

  // send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async ({
      content,
      files,
      messageChecksum,
      messageType,
      extendedMessagePayload,
    }: {
      content?: string;
      files?: File[];
      messageChecksum: string; // served as optimistic message id before be response a real id
      messageType: MessageType;
      extendedMessagePayload?: ExtendedMessagePayload;
      retryMessageId?: Message['id'];
      contactFormMessageId?: Message['id'];
    }) => {
      const formData = new FormData();

      formData.append('channel', 'livechat_v2');
      formData.append('channelIdentityId', channelIdentityId || '');
      formData.append('messageChecksum', messageChecksum);
      formData.append('messageType', messageType);

      if (content) {
        formData.append('messageContent', content);
      }

      if (files?.length) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      if (extendedMessagePayload) {
        formData.append(
          'extendedMessagePayload',
          JSON.stringify(extendedMessagePayload),
        );
      }

      const response = await axiosClient.post(
        `/LiveChatV2/Public/Senders/${senderId}/Channels/${channelIdentityId}/Messages`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      return response.data;
    },

    onMutate: async ({
      content,
      files,
      messageChecksum,
      messageType,
      retryMessageId,
      contactFormMessageId,
    }) => {
      if (contactFormMessageId) {
        return { contactFormMessageId };
      } else if (retryMessageId) {
        // when retry, update the status of the failed message to sending
        messagesActions.updateMessageStatus(
          retryMessageId,
          MessageStatus.SENDING,
        );

        return { retryMessageId };
      } else {
        const newOptimisticMessage: Message = {
          ...getMessageTemplate(),
          channelIdentityId: channelIdentityId || '',
          id: messageChecksum,
          isSentFromSleekflow: false,
          messageChecksum: messageChecksum,
          messageContent: content,
          messageType: messageType,
          originalUploadedFiles: files,
          status: MessageStatus.SENDING,
          uploadedFiles:
            files?.map((file) => ({
              fileId: file.name,
              filename: file.name,
              mimeType: file.type,
              fileSize: file.size,
              url: URL.createObjectURL(file),
            })) || [],
        };

        // when send new message, add the new one to store
        messagesActions.appendMessage(newOptimisticMessage);
        return { newOptimisticMessage };
      }
    },

    onSuccess: (data, variables, context) => {
      if (context?.contactFormMessageId) {
        // when send contact form success, set the hasContactFormSubmitted to true
        senderActions.setHasContactFormSubmitted(true);
      } else if (context?.retryMessageId) {
        // when retry success, move the failed message to the end of array
        messagesActions.moveMessageToEnd(context?.retryMessageId);
      }
      // success messages will be updated (new) or added (retry) to store by useMessageSubscription
    },

    onError: (error, variables, context) => {
      if (context?.retryMessageId) {
        messagesActions.updateMessageStatus(
          context?.retryMessageId,
          MessageStatus.FAILED,
        );
      } else if (context?.newOptimisticMessage) {
        messagesActions.updateMessageStatus(
          context?.newOptimisticMessage?.id,
          MessageStatus.FAILED,
        );
      }
    },
  });

  const handleSendMessage = useCallback(
    (content?: string, files?: File[]) => {
      if (!content?.trim() && !files?.length) {
        console.warn('Cannot send empty message');
        return;
      }

      sendMessageMutation.mutate({
        content: content?.trim(),
        files,
        messageChecksum: uuid4(),
        messageType: files?.length ? MessageType.FILE : MessageType.TEXT,
      });
    },
    [sendMessageMutation],
  );

  const handleRetryMessage = useCallback(
    (messageId: Message['id']) => {
      const retryMessage = messagesMap.get(messageId);

      if (retryMessage) {
        sendMessageMutation.mutate({
          content: retryMessage.messageContent,
          files: retryMessage.originalUploadedFiles || undefined,
          messageChecksum: retryMessage.messageChecksum,
          messageType: retryMessage.originalUploadedFiles?.length
            ? MessageType.FILE
            : MessageType.TEXT,
          retryMessageId: retryMessage.id,
        });
      }
    },
    [messagesMap, sendMessageMutation],
  );

  const handleSendContactForm = useCallback(
    (
      messageId: Message['id'],
      extendedMessagePayload: ExtendedMessagePayload,
    ) => {
      sendMessageMutation.mutate({
        messageChecksum: uuid4(),
        messageType: MessageType.INTERACTIVE,
        extendedMessagePayload,
        contactFormMessageId: messageId,
      });
    },
    [sendMessageMutation],
  );

  return {
    isSending: sendMessageMutation.isPending,
    handleSendMessage,
    handleRetryMessage,
    handleSendContactForm,
  };
};
